<template>
    <v-expansion-panels v-model="expandedPanels" multiple density="compact" class="mt-3">
        <v-expansion-panel :value="0">
            <v-expansion-panel-title>
                <v-row class="w-100 align-center" dense>
                    <v-col cols="12" md="2" class="py-1">
                        <span class="fittings-title">Fittinguri</span>
                    </v-col>
                    <v-col cols="12" md="10" @click.stop class="d-flex justify-end py-1">
                        <v-text-field
                            style="max-width: 300px"
                            v-model="search"
                            density="compact"
                            variant="outlined"
                            hide-details
                            clearable
                            prepend-inner-icon="mdi-magnify"
                            label="Caută fitting"
                        />
                    </v-col>
                </v-row>
            </v-expansion-panel-title>
            <v-expansion-panel-text>
                <v-data-table
                    :headers="tableHeaders"
                    :items="filteredPlumbingItems"
                    item-value="fitting"
                    :items-per-page="10"
                    class="elevation-1"
                >
                    <template #top>
                        <v-sheet
                            class="table-actions-bar"
                            color="grey-lighten-5"
                            rounded="lg"
                            border
                            @click.stop
                        >
                            <div class="table-actions-meta">
                                <v-chip
                                    
                                    color="primary"
                                    variant="outlined"
                                    class="mr-2"
                                >
                                    {{ selectedPlumbingItems.length }} produse cu cantitate
                                </v-chip>
                            </div>
                            <v-btn
                                size="small"
                                variant="text"
                                color="warning"
                                prepend-icon="mdi-backup-restore"
                                :disabled="!hasSelectedQuantities"
                                @click.stop="resetAllQuantities"
                            >
                                Resetează toate cantitățile
                            </v-btn>
                        </v-sheet>
                    </template>
                    <template #[`item.productCode`]="{ item }">
                        {{ item.productCode }}
                    </template>
                    <template #[`item.productName`]="{ item }">
                        {{ item.productName }}
                    </template>
                    <template #[`item.quantity`]="{ item }">
                        <v-text-field
                            variant="outlined"
                            density="compact"
                            hide-details
                            type="number"
                            min="0"
                            style="max-width: 130px"
                            v-model.number="item.quantity"
                            @blur="triggerRecalculation"
                            @update:model-value="triggerRecalculation"
                        />
                    </template>
                    <template #[`item.actions`]="{ item }">
                        <v-tooltip text="Resetează cantitate" location="top">
                            <template #activator="{ props }">
                                <v-btn
                                    v-bind="props"
                                    icon
                                    size="small"
                                    variant="text"
                                    color="warning"
                                    :disabled="Number(item.quantity) <= 0"
                                    @click.stop="clearRowQuantity(item)"
                                >
                                    <v-icon size="18">mdi-backup-restore</v-icon>
                                </v-btn>
                            </template>
                        </v-tooltip>
                    </template>
                </v-data-table>
            </v-expansion-panel-text>
        </v-expansion-panel>
    </v-expansion-panels>
</template>

<script>
import apiClient from "@/utils/apiClient";

export default {
    name: "PlumbingSetup",
    data() {
        return {
            plumbingItems: [],
            expandedPanels: [0],
            search: "",
            tableHeaders: [
                { title: "Cod produs", key: "productCode", sortable: true },
                { title: "Denumire", key: "productName", sortable: true },
                { title: "Cantitate", key: "quantity", sortable: false },
                { title: "Acțiuni", key: "actions", sortable: false, align: "end" },
            ],
        };
    },
    emits: ["update-selected-plumbing-items", "trigger-recalculation"],
    props: {
        plumbingFittingProducts: {
            type: Array,
            required: true,
        },
        offerId: {
            type: String,
        },
        initialPlumbingItems: {
            type: Array,
            default: () => [],
        },
    },
    computed: {
        filteredPlumbingItems() {
            const term = String(this.search || "")
                .trim()
                .toLowerCase();
            const matchingItems = !term
                ? [...this.plumbingItems]
                : this.plumbingItems.filter((item) => {
                      const code = String(item.productCode || "").toLowerCase();
                      const name = String(item.productName || "").toLowerCase();
                      return code.includes(term) || name.includes(term);
                  });

            return matchingItems.sort((a, b) => {
                const aHasQuantity = Number(a.quantity) > 0 ? 1 : 0;
                const bHasQuantity = Number(b.quantity) > 0 ? 1 : 0;
                if (aHasQuantity !== bHasQuantity) {
                    return bHasQuantity - aHasQuantity;
                }
                return String(a.productCode || "").localeCompare(
                    String(b.productCode || "")
                );
            });
        },
        selectedPlumbingItems() {
            return this.plumbingItems
                .filter((item) => Number(item.quantity) > 0)
                .map((item) => ({
                    fitting: item.fitting,
                    product_code: item.fitting,
                    quantity: Number(item.quantity),
                }));
        },
        hasSelectedQuantities() {
            return this.plumbingItems.some((item) => Number(item.quantity) > 0);
        },
    },
    async mounted() {
        this.initializeRows();

        // Check if there's an offerId and fetch the plumbing setup if it exists
        if (this.offerId) {
            await this.fetchPlumbingSetup(this.offerId);
        }
    },
    methods: {
        normalizeItemsForComparison(items = []) {
            return (Array.isArray(items) ? items : [])
                .map((item) => ({
                    fitting: item?.fitting ?? item?.product_code,
                    quantity: Number(item?.quantity) > 0 ? Number(item.quantity) : 0,
                }))
                .filter((item) => item.fitting)
                .sort((a, b) => String(a.fitting).localeCompare(String(b.fitting)));
        },
        savedItemsMatchCurrentSelection(savedItems = []) {
            const current = this.normalizeItemsForComparison(this.selectedPlumbingItems);
            const incoming = this.normalizeItemsForComparison(savedItems);
            if (current.length !== incoming.length) return false;

            return current.every((item, index) => {
                return (
                    item.fitting === incoming[index].fitting &&
                    item.quantity === incoming[index].quantity
                );
            });
        },
        initializeRows(savedItems = []) {
            const quantityByProductCode = new Map(
                (Array.isArray(savedItems) ? savedItems : []).map((item) => [
                    item?.fitting ?? item?.product_code,
                    Number(item?.quantity) > 0 ? Number(item.quantity) : 0,
                ])
            );

            this.plumbingItems = this.plumbingFittingProducts.map((product) => ({
                fitting: product.product_code,
                productCode: product.product_code,
                productName: product.product_name,
                quantity: quantityByProductCode.get(product.product_code) ?? 0,
            }));
        },
        triggerRecalculation() {
            this.$emit("trigger-recalculation");
        },
        clearRowQuantity(item) {
            if (!item || Number(item.quantity) <= 0) return;
            item.quantity = 0;
            this.triggerRecalculation();
        },
        resetAllQuantities() {
            if (!this.hasSelectedQuantities) return;

            this.plumbingItems = this.plumbingItems.map((item) => ({
                ...item,
                quantity: 0,
            }));
            this.triggerRecalculation();
        },
        /**
         * Fetches the plumbing setup for a given offer ID and populates the plumbingItems array.
         * If no setup exists (404), keeps the default empty state — plumbing setup is optional
         * when the user only adds products manually and saves without "Calculează produse".
         * @param {String} offerId - The ID of the offer to fetch the plumbing setup for.
         * @returns {Promise<void>}
         */
        async fetchPlumbingSetup(offerId) {
            try {
                const response = await apiClient.get(
                    `/api/plumbing-setup/${offerId}/`,
                    { validateStatus: (status) => status === 200 || status === 404 }
                );
                if (response.status === 404) {
                    return;
                }
                const plumbingSetup = response.data?.plumbing_items;
                this.initializeRows(plumbingSetup);
            } catch (error) {
                console.error("Error fetching plumbing setup:", error);
            }
        },
    },
    watch: {
        plumbingFittingProducts: {
            immediate: true,
            handler(products) {
                if (!Array.isArray(products) || products.length === 0) return;
                if (!this.offerId) {
                    this.initializeRows(this.initialPlumbingItems);
                }
            },
        },
        initialPlumbingItems: {
            immediate: true,
            handler(newItems) {
                // Existing offers load setup from backend endpoint by offerId.
                // Initial imported rows are only for new offer flows.
                if (this.offerId) return;
                if (this.savedItemsMatchCurrentSelection(newItems)) return;
                this.initializeRows(newItems);
            },
        },
        /**
         * Watches for changes in plumbingItems and emits an event to update the parent component.
         * If any item has a falsy property, it is removed from the plumbingItems array.
         * @param {Array} newItems - The updated plumbing items array.
         */
        plumbingItems: {
            handler: function () {
                this.$emit(
                    "update-selected-plumbing-items",
                    this.selectedPlumbingItems
                );
            },
            deep: true,
        },
    },
};
</script>

<style scoped>
.fittings-title {
    flex-shrink: 0;
}

.table-actions-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin: 8px 12px 12px;
    padding: 10px 12px;
}

.table-actions-meta {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    font-size: 0.875rem;
    color: rgba(0, 0, 0, 0.6);
}

@media (max-width: 768px) {
    .table-actions-bar {
        flex-direction: column;
        align-items: stretch;
    }
}
</style>
